import { z } from 'zod';
import {
  ApproverPolicySchema,
  DataClassificationSchema,
  DomainSchema,
  LlmModeSchema,
  ScoringMethodSchema,
  TenantTierSchema,
  UserRoleSchema,
} from './enums';
import { PackBindingSchema, TenantIdSchema, UserIdSchema } from './ids';
import { NonEmptyStringSchema, TimestampSchema } from './primitives';

/**
 * Privacy-aware routing for the dedicated tier (DESIGN_SPEC B.3). Present in the
 * schema so the LLM router can be built against it in sds-08 without a
 * migration; not enforced anywhere yet.
 */
export const PrivacyRoutingSchema = z.object({
  redact_pii_before_cloud: z.boolean(),
  pii_classes: z.array(DataClassificationSchema),
  route_sensitive_to_local: z.boolean(),
  local_model_ref: z.string().nullable(),
});
export type PrivacyRouting = z.infer<typeof PrivacyRoutingSchema>;

/**
 * Provider policy (DESIGN_SPEC §7.2, §13.2).
 *
 * `zero_retention` and `no_training` are `literal(true)`, not booleans. The
 * platform-wide restriction in §13.1 is not a setting a tenant can turn off, so
 * a policy that permits retention or training is made unrepresentable rather
 * than merely discouraged.
 */
export const LlmPolicySchema = z.object({
  provider: NonEmptyStringSchema,
  model: NonEmptyStringSchema.nullable(),
  zero_retention: z.literal(true),
  no_training: z.literal(true),
  mode: LlmModeSchema,
  privacy_routing: PrivacyRoutingSchema.nullable(),
});
export type LlmPolicy = z.infer<typeof LlmPolicySchema>;

export const TenantSchema = z.object({
  tenant_id: TenantIdSchema,
  name: NonEmptyStringSchema,
  tier: TenantTierSchema,
  approver_policy: ApproverPolicySchema,
  default_scoring_method: ScoringMethodSchema,
  llm_policy: LlmPolicySchema,
  retention_days: z.int().positive(),
  pack_bindings: z.array(PackBindingSchema),
  report_branding_ref: z.string().nullable(),

  /**
   * Which of the six domains this tenant may select (docs/DECISIONS.md
   * ZD-010). Stored as `tenants.enabled_domains_json` since `sds-00` and read
   * by `d1TenantConfigLookup`, which fails closed on a row it cannot parse; it
   * joined the schema in `sds-13`, when `POST /v1/tenants` became the first
   * thing to *write* a tenant row from a `Tenant` rather than from a fixture
   * spelling the columns out (ZD-115). Non-empty: a tenant that may select no
   * domain cannot run an assessment, and `initialise` would refuse it anyway.
   */
  enabled_domains: z.array(DomainSchema).min(1),

  /**
   * Dedicated-database seam (docs/DECISIONS.md ZD-001). `null` means the
   * deployment's one bound database, which under ZD-082 is the only case: a
   * deployment holds one client, so a named binding is a mis-provisioning
   * rather than a tier.
   */
  database_binding: z.string().nullable(),

  created_at: TimestampSchema,
});
export type Tenant = z.infer<typeof TenantSchema>;

/**
 * A platform user: the record a credential resolves to (docs/DECISIONS.md
 * ZD-115, ZD-117). The credential itself hangs off this row — a password in
 * `user_credentials`, sessions and one-time tokens beside it — and nothing
 * about the person lives anywhere but here. `external_subject` is the OIDC
 * subject, so SSO attaches without a migration; nothing writes it yet.
 */
export const UserSchema = z.object({
  user_id: UserIdSchema,
  tenant_id: TenantIdSchema,
  email: z.email(),
  display_name: NonEmptyStringSchema,
  role: UserRoleSchema,
  external_subject: z.string().nullable(),
  created_at: TimestampSchema,
  disabled_at: TimestampSchema.nullable(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * What an administrator supplies to create a user (`POST /v1/users`, ZD-115).
 * The id, tenant and timestamps are the system's; the role is the
 * administrator's choice and is the only thing about a person that the
 * capability checks read.
 */
export const NewUserInputSchema = UserSchema.pick({
  email: true,
  display_name: true,
  role: true,
});
export type NewUserInput = z.infer<typeof NewUserInputSchema>;

/**
 * What the provisioner supplies to bootstrap a deployment's tenant
 * (`POST /v1/tenants`, ZD-115). Everything else on the tenant row is a
 * platform default this input deliberately does not expose: which domains a
 * client may select and which provider policy applies are QRS's to configure,
 * and a bootstrap credential is not a tenant administrator.
 */
export const BootstrapTenantInputSchema = z.object({
  name: NonEmptyStringSchema,
  owner: UserSchema.pick({ email: true, display_name: true }),
  approver_policy: ApproverPolicySchema.default('either'),
  retention_days: z.int().positive().default(365),
});
export type BootstrapTenantInput = z.infer<typeof BootstrapTenantInputSchema>;
